// Loch SVG Morphing Service
// This file maintains the morphing logic from the original script

import { gsap } from "gsap";
// Note: MorphSVGPlugin should be registered outside this service
// We assume it's already registered in the main app or a separate file

class MorphingService {
  constructor() {
    this.svgPaths = []
    this.pathDataArray = []
    this.morphTimeline = null
    this.isPlaying = false
    this.currentPathIndex = 0
    this.completionCallbacks = [] // New array to store completion callbacks
  }

  // Load all path data from the provided SVG data object
  async initWithPathData(svgPathsData) {
    this.svgPathsData = svgPathsData
    this.svgPaths = Object.keys(svgPathsData)
    
    // Create array of path data from the objects
    this.pathDataArray = this.svgPaths.map(id => svgPathsData[id])
    
    // Shuffle the paths for randomized morphing
    this.shuffledPaths = this.shuffleArray([...this.pathDataArray])
    
    return this.shuffledPaths.length > 0 ? this.shuffledPaths[0] : null
  }
  
  // Initialize morphing timeline
  initMorphing(pathElement) {
    if (!pathElement || this.shuffledPaths.length === 0) {
      console.error('Cannot init morphing: no path element or paths')
      return
    }
    
    // Set the initial path if not already set
    if (!pathElement.getAttribute('d')) {
      pathElement.setAttribute('d', this.shuffledPaths[0])
    }
    
    // Create a GSAP timeline with morphing animations
    this.morphTimeline = gsap.timeline({
      repeat: -1, // Infinite repeat
      paused: true, // Start paused
      defaults: {
        ease: 'sine.inOut', // Smooth easing
        duration: 2.5, // Default morph duration
      },
      onComplete: this.handleMorphComplete.bind(this),
      onUpdate: this.handleMorphUpdate.bind(this)
    })
    
    // Add morphing animations
    this.shuffledPaths.forEach((path, index) => {
      const nextPath = this.shuffledPaths[(index + 1) % this.shuffledPaths.length]
      this.morphTimeline.to(pathElement, {
        morphSVG: {
          shape: nextPath,
          shapeIndex: 'auto',
          map: "complexity",
          precision: 5,
        },
        onComplete: () => {
          // Trigger completion of this individual morph segment
          this.handleSegmentComplete()
        }
      })
    })
  }
  
  // Handle update of timeline
  handleMorphUpdate() {
    // Could be used for progress tracking if needed
  }
  
  // Handle completion of full timeline
  handleMorphComplete() {
    // Called when the entire timeline completes a cycle
    // We don't need to do anything special here since repeat is set to -1
  }
  
  // Handle completion of a single morph segment
  handleSegmentComplete() {
    // Execute and clear all completion callbacks
    if (this.completionCallbacks.length > 0) {
      this.completionCallbacks.forEach(callback => callback())
      this.completionCallbacks = []
    }
  }
  
  // Register a callback to be executed when the current morphing completes
  onNextComplete(callback) {
    if (typeof callback === 'function') {
      this.completionCallbacks.push(callback)
    }
  }
  
  // Start morphing
  play() {
    if (this.morphTimeline) {
      this.morphTimeline.play()
      this.isPlaying = true
    }
    return this.isPlaying
  }
  
  // Pause morphing - modified to not pause immediately if requested through the UI
  // Instead, the calling code should use onNextComplete to pause after the current transition
  pause() {
    if (this.morphTimeline) {
      this.morphTimeline.pause()
      this.isPlaying = false
    }
    return this.isPlaying
  }
  
  // Toggle play/pause - fixed to properly handle state changes
  toggle() {
    if (this.isPlaying) {
      // We don't pause immediately here - the component should use onNextComplete
      // Just return the current state so the UI knows we're still playing
      return this.isPlaying
    } else {
      // Start playing and return the new state
      return this.play()
    }
  }
  
  // Update morphing with specific paths based on similar holes
  updateWithSimilarHoles(similarHoles, svgPathsData) {
    if (!similarHoles || similarHoles.length === 0) return false
    
    // Extract paths from similar holes
    const similarPathsData = similarHoles.map(hole => svgPathsData[hole.shape_id])
    
    if (similarPathsData.length === 0) return false
    
    // Pause current timeline
    if (this.morphTimeline) {
      this.morphTimeline.pause()
      this.morphTimeline.kill()
    }
    
    // Create new timeline with filtered paths
    this.shuffledPaths = this.shuffleArray([...similarPathsData])
    return true
  }
  
  // Helper function to shuffle array
  shuffleArray(array) {
    let currentIndex = array.length, randomIndex
    
    while (currentIndex !== 0) {
      randomIndex = Math.floor(Math.random() * currentIndex)
      currentIndex--
      
      [array[currentIndex], array[randomIndex]] = [
        array[randomIndex], array[currentIndex]
      ]
    }
    
    return array
  }
}

export default new MorphingService()